import { TableCard } from "../shared";
import { topCveColumnDefs as columnDefs } from "../col_defs";
import useDashboardService from "../services";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import eventEmitter from "../../../components/utils/eventEmitter";
import CVEDetailsDrawer from "../../../components/cve/CVEDetailsDrawer";
import { Box, CircularProgress } from "@mui/material";
import { CVEData } from "./TopCVELaoyout";
import axiosInstance from "../../../axios/axios";
import { useDashboardContext } from "../DashboardContext";

const PAGE_SIZE = 10;
const SCROLL_THROTTLE_MS = 500;

interface TopCveProps {
  isbothCloudAndPremAssetUser?: boolean;
  isOnlyPremAssetUser?: boolean;
  isOnlyCloudUser?: boolean;
}

const TopCve = ({
  isbothCloudAndPremAssetUser,
  isOnlyPremAssetUser: _isOnlyPremAssetUser,
  isOnlyCloudUser: _isOnlyCloudUser,
}: TopCveProps = {}) => {
  const { fetchTopCves } = useDashboardService();
  const [tableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [trigger, setTrigger] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCVE, setSelectedCVE] = useState<any>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCloud, setIsCloud] = useState<boolean | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
  });

  const gridRef = useRef<any>(null);
  const isFetchingNext = useRef(false);
  const isFetchingPrev = useRef(false);
  const isCloudRef = useRef<boolean | null>(isCloud);
  const lastScrollTs = useRef<number>(0);
  const firstVisibleRowRef = useRef<number>(0);
  const scrollPositionRef = useRef<number>(0);

  // Update ref when isCloud changes
  useEffect(() => {
    isCloudRef.current = isCloud;
  }, [isCloud]);

  const { setIsOverlayOpen } = useDashboardContext();

  // Notify parent context when drawer opens/closes to disable drag and drop
  useEffect(() => {
    setIsOverlayOpen(drawerOpen);
  }, [drawerOpen, setIsOverlayOpen]);



  useEffect(() => {
    const handleFilterSaved = () => {
      setLoading(true);
      setTrigger(prev => prev + 1);
    };

    eventEmitter.on('filterSaved', handleFilterSaved);

    return () => {
      eventEmitter.off('filterSaved', handleFilterSaved);
    };
  }, []);

  // Initialize cloud/on-premise toggle
  useEffect(() => {
    if (isbothCloudAndPremAssetUser) {
      setIsCloud(false); // Default to on-premise
    } else if (_isOnlyCloudUser) {
      setIsCloud(true);
    } else {
      setIsCloud(false); // Default to on-premise
    }
  }, [isbothCloudAndPremAssetUser, _isOnlyCloudUser, _isOnlyPremAssetUser]);

  // Initial fetch - FIXED: removed fetchData from dependencies
  useEffect(() => {
    if (isCloud === null) return;

    // Sync ref so fetchData can read latest value
    isCloudRef.current = isCloud;

    setTableData([]);
    setPagination({
      page: 1,
      totalPages: 1,
      hasNext: false,
      hasPrevious: false,
    });

    // Call fetchData directly without adding it to deps
    fetchData({ page: 1, mode: "initial" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCloud, trigger]);

  // Fixed fetchData function
  const fetchData = useCallback(
    async ({
      page = 1,
      mode = "initial",
    }: {
      page?: number;
      mode?: "initial" | "append" | "prepend";
    } = {}) => {
      const currentIsCloud = isCloudRef.current;
      if (currentIsCloud === null) return;

      if (mode === "initial") {
        setLoading(true);
      } else {
        setIsFetchingMore(true);
      }

      try {
        const module = currentIsCloud ? "cloud" : "on_premise";
        const payload = { type: 1 };

        const {
          data,
          pagination: pageInfo,
          error,
        } = await fetchTopCves(payload, module, {
          page,
          page_size: PAGE_SIZE,
        });

        // Capture scroll state before fetching
        try {
          const apiBefore = gridRef.current?.api;
          if (apiBefore && mode !== "initial") {
            firstVisibleRowRef.current = typeof apiBefore.getFirstDisplayedRow === "function" ? apiBefore.getFirstDisplayedRow() : firstVisibleRowRef.current;
            const vRangeBefore = apiBefore.getVerticalPixelRange?.();
            scrollPositionRef.current = vRangeBefore?.top ?? scrollPositionRef.current;
          }
        } catch (err) {
          console.warn("Could not capture pre-fetch scroll state for TopCve", err);
        }

        // Debug: log API response
        console.log("📥 API Response:", {
          module,
          page,
          dataType: Array.isArray(data) ? "array" : typeof data,
          dataLength: Array.isArray(data) ? data.length : "N/A",
          dataSample: Array.isArray(data) && data.length > 0 ? data[0] : data,
          pageInfo,
          error,
        });

        if (error) {
          toast.error((error as any)?.message || "Failed to load CVEs");
          return;
        }

        setPagination({
          page: pageInfo?.page || page,
          totalPages: pageInfo?.total_pages || 1,
          hasNext: pageInfo?.has_next || false,
          hasPrevious: pageInfo?.has_previous || false,
        });

        setTableData((prev) => {
          if (mode === "initial") {
            console.log("✅ Setting initial data:", data?.length ?? 0, "items");
            return data || [];
          }

          const itemsToAdd = data || [];

          if (mode === "append") {
            console.log(
              "➕ Appending:",
              itemsToAdd.length,
              "items to",
              prev.length,
              "existing"
            );
            return [...prev, ...itemsToAdd];
          }
          if (mode === "prepend") {
            console.log(
              "⬆️ Prepending:",
              itemsToAdd.length,
              "items to",
              prev.length,
              "existing"
            );
            return [...itemsToAdd, ...prev];
          }

          return prev;
        });
        // Restore scroll position after DOM updates
        try {
          const apiAfter = gridRef.current?.api;
          if (apiAfter) {
            if (mode === "append") {
              const indexToShow = firstVisibleRowRef.current ?? 0;
              setTimeout(() => apiAfter.ensureIndexVisible?.(indexToShow, "top"), 50);
            } else if (mode === "prepend") {
              const indexToShow = (firstVisibleRowRef.current ?? 0) + (data?.length || 0);
              setTimeout(() => apiAfter.ensureIndexVisible?.(indexToShow, "top"), 50);
            }
          }
        } catch (err) {
          console.warn("Could not restore scroll for TopCve", err);
        }
      } catch (error) {
        console.error("Error fetching:", error);
        toast.error("Error fetching CVEs");
      } finally {
        setLoading(false);
        setIsFetchingMore(false);
        isFetchingNext.current = false;
        isFetchingPrev.current = false;
      }
    },
    [fetchTopCves]
  );

  // Throttled infinite scroll handler - FIXED: use ref for pagination to avoid recreating
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  const handleScrollEnd = useCallback(() => {
    const now = Date.now();
    if (now - lastScrollTs.current < SCROLL_THROTTLE_MS) {
      return;
    }
    lastScrollTs.current = now;

    const gridApi = gridRef.current?.api;
    if (!gridApi) {
      return;
    }

    try {
      // Get the total row count from the data
      const totalRowCount = tableData.length;
      if (totalRowCount === 0) return;

      // Get viewport info from the event or API
      const verticalPixelRange = gridApi.getVerticalPixelRange?.();

      if (!verticalPixelRange) {
        // Fallback: use simple row count check
        const currentPagination = paginationRef.current;

        // If we have all data loaded and user scrolled, check if we need more
        if (currentPagination.hasNext && !isFetchingNext.current) {
          console.log("⬇️ Loading next page:", currentPagination.page + 1);
          isFetchingNext.current = true;
          fetchData({ page: currentPagination.page + 1, mode: "append" });
        }
        return;
      }

      // Calculate scroll position
      const { top, bottom } = verticalPixelRange;
      const scrollTop = top;
      const scrollHeight =
        gridApi.getModel?.()?.getRowCount?.() * 50 || totalRowCount * 50; // Assume 50px row height
      const clientHeight = bottom - top;

      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
      const isNearBottom = scrollPercentage > 0.8; // 80% scrolled
      const isNearTop = scrollTop < 100; // Less than 100px from top

      const currentPagination = paginationRef.current;

      console.log("📜 Scroll:", {
        scrollTop,
        scrollHeight,
        clientHeight,
        scrollPercentage: (scrollPercentage * 100).toFixed(1) + "%",
        isNearBottom,
        isNearTop,
        hasNext: currentPagination.hasNext,
        hasPrev: currentPagination.hasPrevious,
      });

      // Load next page when near bottom
      if (
        isNearBottom &&
        currentPagination.hasNext &&
        !isFetchingNext.current
      ) {
        console.log("⬇️ Loading next page:", currentPagination.page + 1);
        isFetchingNext.current = true;
        fetchData({ page: currentPagination.page + 1, mode: "append" });
        return;
      }

      // Load previous page when near top
      if (
        isNearTop &&
        currentPagination.hasPrevious &&
        !isFetchingPrev.current
      ) {
        console.log("⬆️ Loading previous page:", currentPagination.page - 1);
        isFetchingPrev.current = true;
        fetchData({ page: currentPagination.page - 1, mode: "prepend" });
        return;
      }
    } catch (err) {
      console.error("Scroll handler error:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData.length]);

  const onRowClick = async (cveData: CVEData) => {
    setDrawerOpen(true);
    setSelectedRow(cveData);
    setSelectedCVE(cveData);
    setIsLoading(true);

    try {
      const payload = { cve: cveData.cve_id };
      const response = await axiosInstance.post(
        "securityscan/cve/cve_summary",
        payload
      );

      if (response.data.status === 200) {
        setSelectedCVE({
          ...cveData,
          summaryData: response.data.data.cve_summary_details,
        });
      }
    } catch (error) {
      console.error("Error fetching CVE summary:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedRow(null);
  };

  const memoizedGridOptions = useMemo(
    () => ({
      rowHeight: 50,
      domLayout: "normal",
      suppressScrollOnNewData: true,
      onBodyScrollEnd: handleScrollEnd,
      fullWidthCellRenderer: () => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ),
      isFullWidthRow: (params: any) => params.rowNode?.data?._isLoadingRow === true,
    }),
    [handleScrollEnd]
  );

  // Add loading row at the end when fetching more pages
  const displayData = useMemo(() => {
    if (isFetchingMore) {
      return [...tableData, { _isLoadingRow: true }];
    }
    return tableData;
  }, [tableData, isFetchingMore]);

  return (
    <>
      <TableCard
        title="Top CVE's"
        columnDefs={columnDefs}
        rowData={displayData}
        showSearch={false}
        isRowSelectable={false}
        loading={loading}
        onRowClick={(data) => {
          // Don't trigger row click for loading row
          if (!data._isLoadingRow) {
            onRowClick(data);
          }
        }}
        gridRef={gridRef}
        gridOptions={memoizedGridOptions}
      />
      {selectedRow && (
        <CVEDetailsDrawer
          open={drawerOpen}
          onClose={handleDrawerClose}
          cveData={{
            cve_id: selectedCVE?.cve_id,
            severity: selectedCVE?.severity,
            cvss_score: parseFloat(selectedCVE?.score),
            affected_assets: selectedCVE?.asset_count,
            summaryData: selectedCVE?.summaryData,
            cisa_key: selectedCVE?.cisas_kev,
          }}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default TopCve;
